import type { ReactNode } from 'react';
import { forwardRef } from 'react';

import type {
  TPaddingSystem,
  TSizeSystem
} from '@/modules/perseo-billing/components/form/ui.interface';

import { createPadding } from '../../beta';
import type { TIconName } from '../icon.component';
import { PdsIcon } from '../icon.component';
import { PdsLoading } from '../loading';

import './button.scss';

type TProps = {
  children?: ReactNode;
  disabled?: boolean;
  feedback?: 'success' | 'error';
  typology?: 'primary' | 'secondary' | 'terciary';
  variant?: 'default' | 'textAndIcon' | 'icon';
  fullWidth?: boolean;
  border?: boolean;
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  loading?: boolean;
  type?: 'submit' | 'button' | 'reset';
  direction?: 'row' | 'reverse';
  rounded?: 'full' | 'md';
  ariaLabel?: string;
} & TPaddingSystem &
  TSizeSystem & { icon?: TIconName };
const buttonClass = {
  disabledPrimary: 'co-bg-pds-neutral-200 co-text-pds-neutral-400',
  disabledSecondary:
    'co-bg-pds-neutral-white co-text-pds-neutral-400 co-border-1 co-border-pds-neutral-300',
  disabledTerciary: 'co-text-pds-neutral-300 co-underline',
  primarySucess:
    'co-bg-pds-primary-500 co-text-pds-neutral-white hover:co-bg-pds-primary-600',
  secondarySucess:
    'co-bg-pds-neutral-white co-text-pds-primary-500 co-border-pds-primary-500 co-border-1 hover:co-bg-pds-primary-100 hover:co-border-pds-primary-600',
  terciarySucess:
    'co-text-pds-primary-500 co-underline hover:co-text-pds-primary-600 hover:co-no-underline active:co-text-pds-primary-700 active:co-underline',
  primaryError:
    'co-bg-pds-error-500 co-text-pds-neutral-white hover:co-bg-pds-error-600',
  secondaryError:
    'co-bg-pds-neutral-white co-text-pds-error-500 co-border-pds-error-500 co-border-1 hover:co-bg-pds-error-100 hover:co-border-pds-error-600',
  terciaryError:
    'co-text-pds-error-500 co-underline hover:co-text-pds-error-600 hover:co-no-underline active:co-text-pds-error-700 active:co-underline'
};

export const PdsButton = forwardRef<HTMLButtonElement, TProps>(
  (
    {
      feedback = 'success',
      typology,
      variant = 'default',
      disabled = false,
      fullWidth = false,
      loading = false,
      type = 'button',
      direction = 'row',
      icon,
      children,
      size = 'medium',
      rounded = 'full',
      ariaLabel,
      pl = 16,
      pr = 16,
      pt = 0,
      pb = 0,
      ...props
    },
    ref
  ) => {
    const getButtonClass = (): string => {
      if (disabled) {
        if (typology === 'primary') {
          return buttonClass.disabledPrimary;
        }

        if (typology === 'secondary') {
          return buttonClass.disabledSecondary;
        }

        if (typology === 'terciary') {
          return buttonClass.disabledPrimary;
        }
      }

      if (typology === 'primary') {
        if (feedback === 'success') {
          return buttonClass.primarySucess;
        }

        if (feedback === 'error') {
          return buttonClass.primaryError;
        }
      }

      if (typology === 'secondary') {
        if (feedback === 'success') {
          return buttonClass.secondarySucess;
        }

        if (feedback === 'error') {
          return buttonClass.secondaryError;
        }
      }

      if (typology === 'terciary') {
        if (feedback === 'success') {
          return buttonClass.terciarySucess;
        }

        if (feedback === 'error') {
          return buttonClass.terciaryError;
        }
      }

      return '';
    };

    const createSizeIcon = (): number => {
      if (size === 'medium') return 24;
      if (size === 'large') return 32;
      return 16;
    };
    const createSizeButton = (): string => {
      if (size === 'large') return 'co-h-[56px] co-text-body1';
      if (size === 'medium') return 'co-h-[44px] co-text-body1';
      if (size === 'small') return 'co-h-[36px] co-text-body1';
      return 'co-h-[44px] co-text-body1';
    };

    return (
      <button
        ref={ref}
        aria-label={ariaLabel}
        type={type}
        disabled={disabled}
        className={`
                ${getButtonClass()}
                ${fullWidth ? 'co-w-full' : 'co-w-auto co-min-w-45'}
                ${createSizeButton()}
                co-flex-${'-' + direction}
                ${
                  disabled || loading
                    ? 'co-pointer-events-none'
                    : 'co-pointer-events-auto'
                }
                ${createPadding({ pl, pr, pt, pb })}
                co-rounded-${rounded}
                co-flex
                co-items-center
                co-justify-center
                co-gap-2
                co-focus:outline-none
                co-font-montserrat
                co-font-bold
                co-button-ui
                `}
        {...props}
      >
        {variant === 'icon' && icon ? (
          loading ? (
            <PdsIcon icon="PdsUndo" />
          ) : (
            <PdsIcon
              width={createSizeIcon()}
              height={createSizeIcon()}
              icon={icon}
            />
          )
        ) : null}
        {loading ? (
          <PdsLoading />
        ) : (
          <>
            {icon && (
              <PdsIcon
                width={createSizeIcon()}
                height={createSizeIcon()}
                icon={icon}
              />
            )}
            {children}
          </>
        )}
      </button>
    );
  }
);
